import { DataGridColumn } from '@amzn/katal-components/es/data-grid';
import React, { useContext } from 'react';
import { CaseListConstants } from 'src/constants/ProjectConstants';
import { renderJsx } from 'src/utilities/PageLayout';
import LinkTextColumnFormatter from '../../../components/CaseListTable/formatters/LinkTextColumnFormatter';
import { FORMATTED_CASE_DETAILS_ROUTE } from 'src/constants/UrlConstants';
import EmailIdColumnFormatter from '../../../components/CaseListTable/formatters/EmailIdColumnFormatter';
import {
  DateColumnFormatter,
  dateComparator,
} from '../../../components/CaseListTable/formatters/DateColumnFormatter';
import { IGetCaseListItem } from 'src/state/slice/models/CaseLifecycleTypes';
import { KatDataGridFilters } from 'src/constants/GlobalConstants';
import { TranslationContext } from 'src/state/providers/TranslationProvider';

export function getColumnsConfig(allowedColumns: Set<string>) {
  const { caseListBundle } = useContext(TranslationContext);
  function formatCaseIdColumn(rowIndex: number, rowData: IGetCaseListItem) {
    return (
      <LinkTextColumnFormatter
        data-testid="caseIdColumn"
        url={`#${FORMATTED_CASE_DETAILS_ROUTE(rowData.caseId)}`}
        text={rowData.caseId}
        target="_blank"
      />
    );
  }

  function formatCaseTitleColumn(rowIndex: number, rowData: IGetCaseListItem) {
    return (
      <LinkTextColumnFormatter
        url={`#${FORMATTED_CASE_DETAILS_ROUTE(rowData.caseId)}`}
        text={rowData.caseTitle}
        requiresTruncatedText={true}
        textLength={100}
        target="_blank"
      />
    );
  }

  function formatCreatedByColumn(rowIndex: number, rowData: IGetCaseListItem) {
    return <EmailIdColumnFormatter emailId={rowData.createdByUser} />;
  }
  function formatLastUpdatedByColumn(
    rowIndex: number,
    rowData: IGetCaseListItem,
  ) {
    return <>{rowData.lastUpdatedByUser}</>;
  }

  function formatCreatedDateColumn(
    rowIndex: number,
    rowData: IGetCaseListItem,
  ) {
    return <DateColumnFormatter date={rowData.createdOn}></DateColumnFormatter>;
  }

  function formatUpdatedDateColumn(
    rowIndex: number,
    rowData: IGetCaseListItem,
  ) {
    return (
      <DateColumnFormatter date={rowData.lastUpdatedOn}></DateColumnFormatter>
    );
  }

  const columnsConfig: Array<DataGridColumn> = [
    {
      headerName: caseListBundle.getMessage(CaseListConstants.CASE_TEXT),
      field: 'caseId',
      sortable: true,
      filter: true,
      renderCustomContent: renderJsx(formatCaseIdColumn),
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.PARAGON_CASE_IDS),
      field: 'paragonCaseIds',
      sortable: true,
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.TITLE_TEXT),
      field: 'caseTitle',
      sortable: true,
      wrapText: true,
      autoHeight: true,
      renderCustomContent: renderJsx(formatCaseTitleColumn),
      tooltipValueGetter: (params: any) => params.data.caseTitle,
      tooltipShowDelay: 0,
      flex: 2,
    },
    {
      headerName: caseListBundle.getMessage(
        CaseListConstants.CREATED_DATE_TEXT,
      ),
      field: 'createdOn',
      sortable: true,
      wrapText: true,
      autoHeight: true,
      renderCustomContent: renderJsx(formatCreatedDateColumn),
      filter: KatDataGridFilters.AG_DATE_COLUMN_FILTER,
      filterParams: {
        comparator: dateComparator,
      },
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.CREATED_BY_TEXT),
      field: 'createdByUser',
      sortable: true,
      renderCustomContent: renderJsx(formatCreatedByColumn),
    },
    {
      headerName: caseListBundle.getMessage(
        CaseListConstants.LAST_UPDATED_TEXT,
      ),
      field: 'lastUpdatedOn',
      sortable: true,
      wrapText: true,
      autoHeight: true,
      renderCustomContent: renderJsx(formatUpdatedDateColumn),
      filter: KatDataGridFilters.AG_DATE_COLUMN_FILTER,
      filterParams: {
        comparator: dateComparator,
      },
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.LAST_UPDATED_BY),
      field: 'lastUpdatedByUser',
      sortable: true,
      renderCustomContent: renderJsx(formatLastUpdatedByColumn),
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.CASE_STATUS),
      field: 'caseStatus',
      sortable: true,
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.ASSIGNEE),
      field: 'assignee',
      sortable: true,
    },
    {
      headerName: caseListBundle.getMessage(CaseListConstants.CASE_LOCALE),
      field: 'caseLocale',
      sortable: true,
    },
  ].filter((oneColumnConfig) => allowedColumns.has(oneColumnConfig.field));

  return columnsConfig;
}
